import React from 'react';
import List from '@material-ui/core/List';
import { Collapse, ListItem, ListItemText, ListSubheader } from '@material-ui/core';
import { ascendingComparator } from '../../document-index/DocumentIndexUtils';
import { IUser } from '../../../api-client/autogenerated';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { getUserCompanyName, isPublicPage, sortLastNamesDesc } from '../../../scripts/utils';

type Props = {
  open: boolean;
  users?: IUser[];
  nonCenterlineUsers?: string[];
  limitHeight?: boolean;
  whiteText?: boolean;
  isDocumentDetail?: boolean;
  handleDelete?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: string) => void;
};

const useStyles = makeStyles<undefined, Props>({
  companyText: {
    fontSize: '20px',
    lineHeight: '32px',
    textTransform: 'none',
    color: (props) => (props.whiteText ? 'white' : 'gray'),
  },
  userText: {
    fontSize: '14px',
    lineHeight: '16px',
    textTransform: 'none',
    color: (props) => (props.whiteText ? 'white' : 'gray'),
  },
});

export default function DistributionList(props: Props) {
  const classes = useStyles(props);
  const { open, users, nonCenterlineUsers, limitHeight, isDocumentDetail, handleDelete } = props;

  const getUserRows = () => {
    const rowData: {
      companyName: string;
      users: { id: string; name: string; email?: string }[];
    }[] = [];
    users?.forEach((user) => {
      const companyName = getUserCompanyName(user);
      const existingRow = rowData.find((row) => row.companyName === companyName);
      if (existingRow) existingRow.users.push(user);
      else rowData.push({ companyName, users: [user] });
    });

    if (nonCenterlineUsers?.length) {
      rowData.push({
        companyName: 'Non-Centerline Users',
        users: nonCenterlineUsers.map((name, index) => ({ id: index.toString(), name })),
      });
    }

    return rowData.sort((a, b) => ascendingComparator(a, b, 'companyName'));
  };

  return (
    <Collapse
      in={open}
      unmountOnExit
      style={limitHeight ? { maxHeight: '300px', overflowY: 'auto' } : undefined}
    >
      {getUserRows().map(({ companyName, users }, index, array) => (
        <List
          dense
          disablePadding
          key={companyName}
          style={{
            paddingLeft: 5,
            marginLeft: '26px',
            marginBottom: index === array.length - 1 && isDocumentDetail ? '12px' : 0,
          }}
        >
          <ListSubheader disableSticky disableGutters className={classes.companyText}>
            {companyName}
          </ListSubheader>
          {users
            .sort((a, b) => sortLastNamesDesc(a.name, b.name))
            .map((user) => (
              <ListItem key={user.id} style={{ paddingTop: 0, paddingBottom: 0 }}>
                <ListItemText
                  primary={user.email ? user.name + ` (${user.email})` : user.name}
                  classes={{ primary: classes.userText }}
                />
              </ListItem>
            ))}
        </List>
      ))}
      {(!users || users.length === 0) && !nonCenterlineUsers?.length && (
        <Typography className={classes.companyText} style={{ margin: '6px', marginLeft: '6px' }}>
          {isPublicPage() ? 'Sign in to view members of this group.' : 'No users'}
        </Typography>
      )}
    </Collapse>
  );
}
